import type { Edge, Node } from '@xyflow/react';
import mitt from 'mitt';
import type { Emitter } from 'mitt';

/**
 * Recipe Editor Events
 * Type-safe event emitter for recipe editor changes
 */
export type RecipeEditorEvents = {
  // Node events
  'node:added': { node: Node };
  'node:deleted': { nodeId: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Node data is dynamic
  'node:updated': { nodeId: string; data: any };
  'nodes:changed': { nodes: Node[] };

  // Edge events
  'edge:added': { edge: Edge };
  'edge:deleted': { edgeId: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Edge data is dynamic
  'edge:updated': { edgeId: string; data: any };
  'edges:changed': { edges: Edge[] };

  // Recipe events
  'recipe:modified': void;
  'recipe:saved': void;
  'recipe:loaded': void;

  // Editor events
  'editor:mode-changed': { mode: 'visual' | 'yaml' };
};

// Create singleton event bus
let eventBus: Emitter<RecipeEditorEvents> | null = null;

// Flag to track if we're currently loading a recipe (to prevent false modification events)
let isLoadingRecipe = false;

// Counter to ignore the first N modification events after loading (ReactFlow fires extra events)
let ignoreNextModifications = 0;

// Flag to distinguish post-save refetch from fresh recipe load.
// When true, setRecipeLoading() will NOT reset the ignoreNextModifications counter,
// preventing legitimate user modifications from being silently swallowed.
let justSaved = false;

/**
 * Get the recipe editor event bus (singleton)
 */
export const getRecipeEditorEventBus = (): Emitter<RecipeEditorEvents> => {
  if (!eventBus) {
    eventBus = mitt<RecipeEditorEvents>();
  }
  return eventBus;
};

/**
 * Check if this is a post-save refetch (not a fresh recipe load).
 * Used by the load effect to skip redundant node/edge reconstruction.
 */
export const isPostSaveRefetch = (): boolean => {
  return justSaved;
};

/**
 * Clear the post-save refetch flag.
 * Called after the load effect has handled the post-save case.
 */
export const clearPostSaveRefetch = () => {
  justSaved = false;
};

/**
 * Set the loading state
 * When true, modification events are suppressed to prevent false positives during recipe load.
 * After a save, the counter is NOT reset — the post-save refetch should not suppress
 * the user's next modification events.
 */
export const setRecipeLoading = (loading: boolean) => {
  const _prevLoading = isLoadingRecipe;
  const _prevIgnore = ignoreNextModifications;
  isLoadingRecipe = loading;
  if (loading) {
    if (justSaved) {
      // Post-save refetch: don't suppress future modifications
      ignoreNextModifications = 0;
    } else {
      // Fresh recipe load: ignore the next 2 modification events (ReactFlow fires extras)
      ignoreNextModifications = 2;
    }
  }
};

/**
 * Check if recipe is currently loading
 */
export const isRecipeLoading = (): boolean => {
  return isLoadingRecipe;
};

/**
 * Emit a recipe modified event
 * This is the main event that triggers the save button.
 * Skips emission if recipe is currently loading OR if we should ignore this event.
 *
 * @param options.force - If true, bypass ALL suppression (isLoadingRecipe + ignoreNextModifications).
 *   Use for explicit user actions (node form save, drag-drop) that must always be respected.
 *   The ignoreNextModifications counter is designed for ReactFlow's internal change events
 *   (dimensions, reset) after recipe load — it should NOT suppress explicit user actions.
 */
export const emitRecipeModified = (options?: { force?: boolean }) => {
  if (options?.force) {
    getRecipeEditorEventBus().emit('recipe:modified');
    return;
  }

  const suppressed = isLoadingRecipe || ignoreNextModifications > 0;
  if (suppressed) {
    if (ignoreNextModifications > 0) {
      ignoreNextModifications--;
    }
    return;
  }

  getRecipeEditorEventBus().emit('recipe:modified');
};

/**
 * Emit a recipe saved event
 */
export const emitRecipeSaved = () => {
  justSaved = true; // Signal that the next refetch is post-save
  ignoreNextModifications = 0; // Reset ignore counter after save
  getRecipeEditorEventBus().emit('recipe:saved');
};

/**
 * Emit a recipe loaded event
 * Automatically sets loading state to false but continues to ignore initial modification events
 */
export const emitRecipeLoaded = () => {
  isLoadingRecipe = false; // Recipe is now loaded, but still ignoring next few modifications
  getRecipeEditorEventBus().emit('recipe:loaded');
};
